/**
 * Width and alignment of each dashboard page's content container, for pages
 * narrower than the default 1360px layout. The promo banner must match "the
 * width of the current page container" and its alignment (r-1913 Figma,
 * "Smaller Container" section / "Banner placement" note), so PromoBanner
 * looks these up by pathname.
 *
 * Each entry mirrors the container the page itself renders — the source is
 * cited so drift is traceable. Pages not listed use the full-width default.
 */
type PageContentContainer = {
  maxWidth: number;
  centered: boolean;
};

const NARROW_PAGE_CONTAINERS: {
  pattern: RegExp;
  container: PageContentContainer;
}[] = [
  // ReferralPage / RedesignedReferralPage: maxWidth 700, mx auto
  {
    pattern: /^\/earn\/referral$/,
    container: { maxWidth: 700, centered: true },
  },
  // ProfilePage: maxWidth 700, mx auto
  { pattern: /^\/profile$/, container: { maxWidth: 700, centered: true } },
  // DeleteAccountPage: Grid maxWidth 1000 with its own px 3 (16px per side on
  // this theme's spacing scale, border-box), so the card inside spans 968px,
  // left-aligned
  {
    pattern: /^\/profile\/deleteaccount/,
    container: { maxWidth: 968, centered: false },
  },
  // ConfirmChangeEmail: Grid maxWidth 800, left-aligned
  {
    pattern: /^\/profile\/changeemail/,
    container: { maxWidth: 800, centered: false },
  },
  // ProxyReplacePageContent: maxWidth 600, centered (also under /$planId)
  {
    pattern: /\/proxy\/list\/replace$/,
    container: { maxWidth: 600, centered: true },
  },
  // InvoiceDetail: Paper maxWidth 800, margin 0 auto
  {
    pattern: /^\/subscription\/invoices\/./,
    container: { maxWidth: 800, centered: true },
  },
];

export default function getPageContentContainer(
  pathname: string
): PageContentContainer | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    NARROW_PAGE_CONTAINERS.find(({ pattern }) => pattern.test(normalized))
      ?.container ?? null
  );
}
